import * as THREE from 'three';

import React, { useEffect, useRef, useState } from 'react';
import { Sky, Text } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';

import { SimpleNaruto } from '../../SimpleNaruto';
import { SimpleSakura } from '../../SimpleSakura';
import { SimpleSasuke } from '../../SimpleSasuke';
import { useKeyboardControls } from '../../../engine/useKeyboardControls';

interface ContraMissionSceneProps {
  onExit: () => void;
  characterType?: string; // Add character type prop
  incrementPlayerScore?: (points: number) => void; // Add scoring function
}

// Define types for our game entities
interface Player {
  position: THREE.Vector3;
  health: number;
  projectiles: Projectile[];
  facingDirection: THREE.Vector3;
}

interface Snake {
  id: string;
  position: THREE.Vector3;
  health: number;
  direction: THREE.Vector3;
  speed: number;
}

interface Projectile {
  id: string;
  position: THREE.Vector3;
  direction: THREE.Vector3;
  speed: number;
  type: 'shuriken' | 'rasengan';
}

interface Boss {
  position: THREE.Vector3;
  health: number;
  attackTimer: number;
  state: 'idle' | 'attacking';
}

export const ContraMissionScene: React.FC<ContraMissionSceneProps> = ({
  onExit,
  characterType = 'naruto', // Default to Naruto if not specified
  incrementPlayerScore,
}) => {
  const { camera } = useThree();

  // Game state
  const [gameStarted, setGameStarted] = useState(false);

  // Calculate initial direction toward front of the scene (always face forward)
  const initialPlayerPosition = new THREE.Vector3(0, 1, 8);
  // Forward direction is toward negative Z (into the scene)
  const initialDirection = new THREE.Vector3(0, 0, -1);

  const [player, setPlayer] = useState<Player>({
    position: initialPlayerPosition,
    health: 100,
    projectiles: [],
    facingDirection: initialDirection,
  });
  const [snakes, setSnakes] = useState<Snake[]>([]);
  const [boss, setBoss] = useState<Boss>({
    position: new THREE.Vector3(0, 1, -20),
    health: 100,
    attackTimer: 0,
    state: 'idle',
  });
  const [platforms, setPlatforms] = useState<THREE.Vector3[]>([
    new THREE.Vector3(-4, 1, 0),
    new THREE.Vector3(4, 1, -5),
    new THREE.Vector3(-5, 1, -10),
    new THREE.Vector3(5, 1, -15),
  ]);

  // Movement animation states for Orochimaru
  const [bossArmRotation, setBossArmRotation] = useState(0);
  const [bossSwayAngle, setBossSwayAngle] = useState(0);

  // Keep track of last fire time for cooldowns
  const lastFireTime = useRef<number>(0);
  const lastRasenganTime = useRef<number>(0);

  // Get keyboard controls
  const controls = useKeyboardControls();

  // Hit effects for visual feedback
  const [hitEffects, setHitEffects] = useState<
    { position: THREE.Vector3; createdAt: number }[]
  >([]);

  // Set up camera for this scene
  useEffect(() => {
    console.log('Setting up ContraMissionScene camera');
    camera.position.set(0, 4, 20);
    camera.lookAt(0, 0, 0);

    return () => {
      console.log('Cleaning up ContraMissionScene');
    };
  }, [camera]);

  // Handle player input and game logic
  useFrame((_, delta) => {
    if (!gameStarted) return;

    // Update player position based on controls
    const newPosition = player.position.clone();
    const moveSpeed = 0.15;

    if (controls.left) {
      newPosition.x -= moveSpeed;
    }
    if (controls.right) {
      newPosition.x += moveSpeed;
    }
    if (controls.forward) {
      newPosition.z -= moveSpeed;
    }
    if (controls.backward) {
      newPosition.z += moveSpeed;
    }

    // Constrain player to corridor
    newPosition.x = Math.max(-14, Math.min(14, newPosition.x));
    newPosition.z = Math.max(-30, Math.min(15, newPosition.z));

    // Always use forward direction (toward front of scene)
    const forwardDirection = new THREE.Vector3(0, 0, -1);

    // Update player position and facing direction (always forward)
    setPlayer((prev) => ({
      ...prev,
      position: newPosition,
      facingDirection: forwardDirection,
    }));

    // Fire shuriken
    if (controls.shuriken && Date.now() - lastFireTime.current > 300) {
      const newProjectile: Projectile = {
        id: `shuriken-${Date.now()}`,
        position: newPosition.clone(),
        direction: forwardDirection.clone(), // Use forward direction
        speed: 0.3,
        type: 'shuriken',
      };

      setPlayer((prev) => ({
        ...prev,
        projectiles: [...prev.projectiles, newProjectile],
      }));

      lastFireTime.current = Date.now();
    }

    // Fire rasengan
    if (controls.rasengan && Date.now() - lastRasenganTime.current > 2000) {
      const newProjectile: Projectile = {
        id: `rasengan-${Date.now()}`,
        position: newPosition.clone(),
        direction: forwardDirection.clone(), // Use forward direction
        speed: 0.2,
        type: 'rasengan',
      };

      setPlayer((prev) => ({
        ...prev,
        projectiles: [...prev.projectiles, newProjectile],
      }));

      lastRasenganTime.current = Date.now();
    }

    // Update projectiles
    setPlayer((prev) => {
      const updatedProjectiles = prev.projectiles
        .map((projectile) => {
          const newPos = projectile.position
            .clone()
            .add(projectile.direction.clone().multiplyScalar(projectile.speed));
          return {
            ...projectile,
            position: newPos,
          };
        })
        .filter((projectile) => {
          // Remove projectiles that go out of bounds
          return (
            projectile.position.z > -35 &&
            projectile.position.z < 15 &&
            projectile.position.x > -15 &&
            projectile.position.x < 15
          );
        });

      return {
        ...prev,
        projectiles: updatedProjectiles,
      };
    });

    // Update snakes
    setSnakes((prevSnakes) => {
      return prevSnakes.map((snake) => {
        // Calculate a slithering motion by adding a sine wave to the movement
        const time = Date.now() / 1000;
        const slitherOffset = new THREE.Vector3(
          Math.sin(time * 2 + parseInt(snake.id.split('-')[1]) * 0.1) * 0.05,
          0,
          0
        );

        // Apply forward motion plus slithering
        const newPos = snake.position
          .clone()
          .add(snake.direction.clone().multiplyScalar(snake.speed))
          .add(slitherOffset);

        // Bounce off walls
        let newDirection = snake.direction.clone();
        if (newPos.x < -14 || newPos.x > 14) {
          newDirection.x *= -1;
        }
        if (newPos.z < -30 || newPos.z > 15) {
          newDirection.z *= -1;
        }

        // Apply bounds
        newPos.x = Math.max(-14, Math.min(14, newPos.x));
        newPos.z = Math.max(-30, Math.min(15, newPos.z));

        return {
          ...snake,
          position: newPos,
          direction: newDirection,
        };
      });
    });

    // Update boss
    setBoss((prevBoss) => {
      // Create more dynamic, organic boss movement with varied patterns
      const newPos = prevBoss.position.clone();
      const time = Date.now() / 1000;

      // Create a more aggressive movement pattern with faster speed and occasional dashes
      // Base movement - wider range (10 instead of 8) and faster oscillation
      const baseMovement = 10 * Math.sin(time / 2);

      // Add secondary patterns for organic feel
      const secondaryMovement = 3 * Math.sin(time / 0.7);

      // Add quick "dash" effect every few seconds
      const dashEffect =
        Math.sin(time * 0.3) > 0.9 ? 4 * Math.sin(time * 5) : 0; // Occasional quick movements

      // Combine all movement patterns
      newPos.x = baseMovement + secondaryMovement + dashEffect;

      // More pronounced vertical movement
      newPos.y = 1 + 0.3 * Math.sin(time / 1.2) + 0.1 * Math.cos(time / 0.5);

      // Occasional forward/backward movement
      newPos.z = prevBoss.position.z + Math.sin(time * 0.4) * 0.1;

      // Apply constraints to keep boss within level boundaries
      // Keep Orochimaru within side walls
      newPos.x = Math.max(-12, Math.min(12, newPos.x));

      // Keep Orochimaru within z-axis boundaries (prevent going behind wall)
      // The snake head wall is at around z = -34.8, so keep some distance
      newPos.z = Math.max(-33, Math.min(-15, newPos.z));

      // Keep vertical position reasonable
      newPos.y = Math.max(0.5, Math.min(2, newPos.y));

      // More aggressive body sway during movement
      const swayIntensity = 0.15 + (Math.abs(dashEffect) > 0.5 ? 0.1 : 0);
      setBossSwayAngle(
        Math.sin(time * 1.5) * swayIntensity + Math.sin(time * 0.9) * 0.07
      );

      // Dynamic arm movements - more pronounced when moving faster
      const armMovementSpeed = 1 + Math.abs(newPos.x - prevBoss.position.x) * 5;
      setBossArmRotation(
        0.3 * Math.sin(time * armMovementSpeed) + 0.15 * Math.sin(time * 0.7)
      );

      // Attack logic - spawn snakes periodically
      const newAttackTimer = prevBoss.attackTimer + delta;
      let newState = prevBoss.state;

      if (newAttackTimer > 3 && prevBoss.state === 'idle') {
        // Time to attack
        newState = 'attacking';

        // Spawn a new snake
        const newSnake: Snake = {
          id: `snake-${Date.now()}`,
          position: new THREE.Vector3(
            newPos.x + Math.random() * 2 - 1, // Around the boss position with some variation
            1, // On the ground
            newPos.z + 2 // In front of the boss
          ),
          health: 100,
          direction: new THREE.Vector3(
            Math.random() * 0.4 - 0.2, // Slight random x direction
            0,
            1 // Toward player
          ).normalize(),
          speed: 0.08,
        };

        setSnakes((prev) => [...prev, newSnake]);

        // Reset timer
        return {
          ...prevBoss,
          position: newPos,
          attackTimer: 0,
          state: 'idle', // Immediately go back to idle after attack
        };
      }

      return {
        ...prevBoss,
        position: newPos,
        attackTimer: newAttackTimer,
        state: newState,
      };
    });

    // Check for projectile hitting snakes
    player.projectiles.forEach((projectile) => {
      setSnakes((prevSnakes) => {
        return prevSnakes
          .map((snake) => {
            // Calculate distance between projectile and snake center
            const distance = projectile.position.distanceTo(snake.position);

            // Also check if the projectile is close to the head, which is positioned forward
            const headPosition = snake.position
              .clone()
              .add(snake.direction.clone().multiplyScalar(0.7));
            const headDistance = projectile.position.distanceTo(headPosition);

            // Check if projectile hit the snake (body or head)
            if (distance < 0.6 || headDistance < 0.5) {
              // Show hit effect for snake
              setHitEffects((prev) => [
                ...prev,
                {
                  position: projectile.position.clone(),
                  createdAt: Date.now(),
                },
              ]);

              // Remove projectile
              setPlayer((prevPlayer) => ({
                ...prevPlayer,
                projectiles: prevPlayer.projectiles.filter(
                  (p) => p.id !== projectile.id
                ),
              }));

              // Update score when snake is hit
              if (incrementPlayerScore) {
                incrementPlayerScore(1); // Award 1 point for hitting a snake
              }

              // Reduce snake health
              if (snake.health <= 25) {
                // Snake is defeated
                return {
                  ...snake,
                  health: 0,
                };
              } else {
                return {
                  ...snake,
                  health: snake.health - 50,
                };
              }
            }
            return snake;
          })
          .filter((snake) => snake.health > 0);
      });
    });

    // Projectile vs Boss collision
    player.projectiles.forEach((projectile) => {
      const distanceToBoss = projectile.position.distanceTo(boss.position);
      if (distanceToBoss < 2.5) {
        // Show hit effect
        setHitEffects((prev) => [
          ...prev,
          {
            position: projectile.position.clone(),
            createdAt: Date.now(),
          },
        ]);

        // Apply damage based on projectile type
        const damage = projectile.type === 'shuriken' ? 5 : 20;

        // Update score when boss is hit
        if (incrementPlayerScore) {
          const points = projectile.type === 'shuriken' ? 1 : 2;
          incrementPlayerScore(points); // Award more points for rasengan hits
        }

        setBoss((prevBoss) => ({
          ...prevBoss,
          health: Math.max(0, prevBoss.health - damage),
        }));

        // Remove projectile
        setPlayer((prevPlayer) => ({
          ...prevPlayer,
          projectiles: prevPlayer.projectiles.filter(
            (p) => p.id !== projectile.id
          ),
        }));
      }
    });

    // Collision detection - snakes vs player
    snakes.forEach((snake) => {
      const distance = snake.position.distanceTo(player.position);
      if (distance < 1) {
        // Player hit by snake!
        setPlayer((prevPlayer) => ({
          ...prevPlayer,
          health: Math.max(0, prevPlayer.health - 30),
        }));

        // Remove the snake
        setSnakes((prevSnakes) => prevSnakes.filter((s) => s.id !== snake.id));
      }
    });

    // Check for game over or win condition
    if (player.health <= 0) {
      // Game over - reset
      setGameStarted(false);

      // Reset player score to 0 when they die
      if (incrementPlayerScore) {
        // Send a special reset command for Contra mission
        incrementPlayerScore(-999999); // Using a large negative number to ensure it goes to 0
      }

      setPlayer({
        position: initialPlayerPosition,
        health: 100,
        projectiles: [],
        facingDirection: initialDirection,
      });
      setBoss({
        position: new THREE.Vector3(0, 1, -30),
        health: 100,
        attackTimer: 0,
        state: 'idle',
      });
    }

    if (boss.health <= 0) {
      // Win!
      // For now, just reset the game
      setGameStarted(false);

      setPlayer({
        position: initialPlayerPosition,
        health: 100,
        projectiles: [],
        facingDirection: initialDirection,
      });
      setBoss({
        position: new THREE.Vector3(0, 1, -30),
        health: 100,
        attackTimer: 0,
        state: 'idle',
      });
    }

    // Cleanup expired hit effects
    setHitEffects((prev) => {
      return prev.filter((effect) => {
        return Date.now() - effect.createdAt < 500; // Keep only effects less than 500ms old
      });
    });
  });

  // Add a keydown listener to allow ESC to exit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        console.log('ESC key pressed, exiting mission');
        onExit();
      }
      if (e.key === 'Enter' && !gameStarted) {
        setGameStarted(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onExit, gameStarted]);

  // Render the appropriate character based on characterType
  const renderCharacter = () => {
    // Common props for all character components
    const characterProps = {
      position: [player.position.x, player.position.y, player.position.z] as [
        number,
        number,
        number
      ],
      facingDirection: [
        player.facingDirection.x,
        player.facingDirection.y,
        player.facingDirection.z,
      ] as [number, number, number],
      isControlled: false, // We control it ourselves in this scene
      health: player.health,
      isDead: player.health <= 0,
    };

    switch (characterType) {
      case 'sasuke':
        return <SimpleSasuke {...characterProps} />;
      case 'sakura':
        return <SimpleSakura {...characterProps} />;
      case 'naruto':
      default:
        return <SimpleNaruto {...characterProps} />;
    }
  };

  return (
    <>
      {/* Snake temple-themed environment */}
      <ambientLight intensity={0.5} /> {/* Dimmer lighting for temple feel */}
      <directionalLight position={[10, 10, 5]} intensity={1.0} castShadow />
      <Sky sunPosition={[0, 0.1, 0]} inclination={0.15} azimuth={0.25} />
      {/* Floor - stone with ornate patterns */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -0.01, -5]}
        receiveShadow
      >
        <planeGeometry args={[40, 60]} />
        <meshStandardMaterial
          color="#A57548"
          roughness={0.7}
          metalness={0.2}
        />{' '}
        {/* Sandy brown stone */}
      </mesh>
      {/* Add floor pattern */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, -5]} receiveShadow>
        <planeGeometry args={[38, 58]} />
        <meshStandardMaterial
          color="#945F36"
          roughness={0.8}
          metalness={0.1}
          transparent
          opacity={0.7}
          wireframe
        />{' '}
        {/* Ornate carved patterns */}
      </mesh>
      {/* Side walls - snake temple stone with cracks */}
      {/* Left wall */}
      <mesh position={[-15, 3, -5]} castShadow>
        <boxGeometry args={[1, 8, 60]} />
        <meshStandardMaterial
          color="#965A38"
          roughness={0.9}
          metalness={0.1}
        />{' '}
        {/* Sandy temple color */}
      </mesh>
      {/* Right wall */}
      <mesh position={[15, 3, -5]} castShadow>
        <boxGeometry args={[1, 8, 60]} />
        <meshStandardMaterial color="#965A38" roughness={0.9} metalness={0.1} />
      </mesh>
      {/* Ceiling */}
      <mesh position={[0, 8, -5]} castShadow>
        <boxGeometry args={[40, 1, 60]} />
        <meshStandardMaterial color="#855033" roughness={0.8} />{' '}
        {/* Darker stone ceiling */}
      </mesh>
      {/* Wall decorations - horizontal wooden beams */}
      {[-30, -20, -10, 0, 10].map((z, i) => (
        <group key={`beam-${i}`}>
          {/* Top beam */}
          <mesh position={[0, 6, z]} castShadow>
            <boxGeometry args={[30, 0.5, 0.5]} />
            <meshStandardMaterial color="#5D4037" roughness={0.9} />{' '}
            {/* Dark wood */}
          </mesh>

          {/* Left and right vertical supports */}
          <mesh position={[-13, 3, z]} castShadow>
            <boxGeometry args={[0.5, 7, 0.5]} />
            <meshStandardMaterial color="#5D4037" roughness={0.9} />
          </mesh>
          <mesh position={[13, 3, z]} castShadow>
            <boxGeometry args={[0.5, 7, 0.5]} />
            <meshStandardMaterial color="#5D4037" roughness={0.9} />
          </mesh>
        </group>
      ))}
      {/* Giant snake head at end of corridor */}
      <group position={[0, 3, -34.8]}>
        {/* Blue background rectangle */}
        <mesh position={[0, 0, -0.1]} castShadow>
          <boxGeometry args={[40, 10, 0.5]} />
          <meshStandardMaterial
            color="#965A38"
            roughness={0.5}
            metalness={0.2}
          />
        </mesh>

        {/* Main head shape */}
        <mesh castShadow>
          <boxGeometry args={[10, 5, 1]} />
          <meshStandardMaterial
            color="#CC0000" // Bright red color for the snake head wall
            roughness={0.8}
            metalness={0.1}
          />
        </mesh>

        {/* Snake eye sockets */}
        <mesh position={[-3, 1, 0.6]} castShadow>
          <cylinderGeometry args={[1.2, 1.2, 0.5, 32]} />
          <meshStandardMaterial color="#000000" />
        </mesh>
        <mesh position={[3, 1, 0.6]} castShadow>
          <cylinderGeometry args={[1.2, 1.2, 0.5, 32]} />
          <meshStandardMaterial color="#000000" />
        </mesh>

        {/* Glowing eyes */}
        <pointLight
          position={[-3, 1, 1]}
          distance={3}
          intensity={1}
          color="#FFA500"
        />
        <pointLight
          position={[3, 1, 1]}
          distance={3}
          intensity={1}
          color="#FFA500"
        />
        <mesh position={[-3, 1, 0.7]} castShadow>
          <sphereGeometry args={[0.8, 32, 32]} />
          <meshStandardMaterial
            color="#FFA500"
            emissive="#FF4500"
            emissiveIntensity={2}
          />
        </mesh>
        <mesh position={[3, 1, 0.7]} castShadow>
          <sphereGeometry args={[0.8, 32, 32]} />
          <meshStandardMaterial
            color="#FFA500"
            emissive="#FF4500"
            emissiveIntensity={2}
          />
        </mesh>

        {/* Snake nose and mouth */}
        <mesh position={[0, -1, 0.6]} rotation={[0, 0, 0]} castShadow>
          <boxGeometry args={[6, 1, 0.5]} />
          <meshStandardMaterial
            color="#8B5A2B"
            roughness={0.9}
            metalness={0.1}
          />
        </mesh>
      </group>
      {/* Stone platforms with cracks */}
      {platforms.map((platform, i) => (
        <mesh key={i} position={platform} castShadow>
          <boxGeometry args={[3, 0.5, 2]} />
          <meshStandardMaterial
            color="#A57548"
            roughness={0.9}
            metalness={0.1}
          />{' '}
          {/* Cracked stone */}
        </mesh>
      ))}
      {/* Wall carvings - replacing Konoha symbols */}
      {[-8, -4, 0, 4, 8].map((x, i) => (
        <mesh key={i} position={[x, 3, -20]} rotation={[0, 0, 0]}>
          <cylinderGeometry args={[0.8, 0.8, 0.2, 6]} />{' '}
          {/* Hexagonal carvings */}
          <meshStandardMaterial
            color="#7F462C"
            roughness={0.8}
            metalness={0.1}
          />{' '}
          {/* Darker stone */}
        </mesh>
      ))}
      {/* Torches on walls - reduced number for performance */}
      {[...Array(4)].map((_, i) => {
        // Reduced from 8 to 4 torches
        const z = -30 + i * 16; // Wider spacing between torches
        return (
          <React.Fragment key={`torches-${i}`}>
            {/* Left wall torch */}
            <group position={[-14.5, 6, z]}>
              {' '}
              {/* Raised height */}
              <mesh position={[0, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
                <cylinderGeometry args={[0.05, 0.1, 0.6, 6]} />
                <meshBasicMaterial color="#5D4037" />{' '}
                {/* Simplified material */}
              </mesh>
              <pointLight
                position={[0, 0.4, 0]}
                intensity={0.9}
                color="#FFA500"
                distance={8}
              />
              <mesh position={[0, 0.5, 0]}>
                <sphereGeometry args={[0.2, 8, 8]} />
                <meshBasicMaterial color="#FFA500" />
              </mesh>
            </group>

            {/* Right wall torch */}
            <group position={[14.5, 6, z]}>
              {' '}
              {/* Raised height */}
              <mesh position={[0, 0, 0]} rotation={[0, -Math.PI / 2, 0]}>
                <cylinderGeometry args={[0.05, 0.1, 0.6, 6]} />
                <meshBasicMaterial color="#5D4037" />{' '}
                {/* Simplified material */}
              </mesh>
              <pointLight
                position={[0, 0.4, 0]}
                intensity={0.9}
                color="#FFA500"
                distance={8}
              />
              <mesh position={[0, 0.5, 0]}>
                <sphereGeometry args={[0.2, 8, 8]} />
                <meshBasicMaterial color="#FFA500" />
              </mesh>
            </group>
          </React.Fragment>
        );
      })}
      {/* Player character */}
      {renderCharacter()}
      {/* Projectiles */}
      {player.projectiles.map((projectile) => (
        <group
          key={projectile.id}
          position={projectile.position}
          rotation={[0, 0, Date.now() / 100]} // Rotating effect
        >
          {projectile.type === 'shuriken' ? (
            // Shuriken style matching SimpleNaruto
            <>
              {/* Center part */}
              <mesh rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.05, 0.05, 0.03, 16]} />
                <meshStandardMaterial
                  color="#555555"
                  metalness={0.8}
                  roughness={0.2}
                />
              </mesh>

              {/* Four blades */}
              {[0, 1, 2, 3].map((i) => (
                <mesh
                  key={i}
                  position={[
                    0.12 * Math.cos((i * Math.PI) / 2),
                    0.12 * Math.sin((i * Math.PI) / 2),
                    0,
                  ]}
                  rotation={[0, 0, (i * Math.PI) / 2]}
                >
                  <boxGeometry args={[0.2, 0.05, 0.01]} />
                  <meshStandardMaterial
                    color="#303030"
                    metalness={0.9}
                    roughness={0.1}
                  />
                </mesh>
              ))}
            </>
          ) : (
            // Rasengan/special attack style based on character type
            <mesh>
              <sphereGeometry args={[0.3, 16, 16]} />
              <meshStandardMaterial
                color={
                  characterType === 'sasuke'
                    ? '#FF4500' // Red-orange for Sasuke's fireball
                    : characterType === 'sakura'
                    ? '#E91E63' // Pink for Sakura's cherry blossom impact
                    : '#00BFFF' // Bright blue for Naruto's rasengan
                }
                emissive={
                  characterType === 'sasuke'
                    ? '#FF7F50' // Coral emissive for Sasuke
                    : characterType === 'sakura'
                    ? '#F8BBD0' // Light pink emissive for Sakura
                    : '#0080FF' // Blue emissive for Naruto
                }
                emissiveIntensity={2}
                toneMapped={false}
              />
            </mesh>
          )}
        </group>
      ))}
      {/* Snakes */}
      {snakes.map((snake) => {
        // Add a slight vertical bobbing based on time and snake ID
        const bobHeight =
          Math.sin(Date.now() / 500 + parseInt(snake.id.split('-')[1]) * 0.5) *
          0.1;
        const snakePosition = snake.position.clone();
        snakePosition.y += bobHeight;
        const isGraySnake = parseInt(snake.id.split('-')[1]) % 2 === 0;

        const primaryColor = isGraySnake ? '#D3D3D3' : '#8B4513'; // Light gray or brown
        const secondaryColor = isGraySnake ? '#C0C0C0' : '#A0522D'; // Darker gray or darker brown

        return (
          <group
            key={snake.id}
            position={[snakePosition.x, snakePosition.y, snakePosition.z]}
            rotation={[0, Math.atan2(snake.direction.x, snake.direction.z), 0]}
          >
            {/* Snake body - centered at origin, rotated to align with Z-axis */}
            <mesh
              position={[0, 0, 0]}
              rotation={[Math.PI / 2, 0, 0]}
              castShadow
            >
              <cylinderGeometry args={[0.25, 0.35, 1.2, 8]} />
              <meshLambertMaterial color={primaryColor} flatShading />
            </mesh>

            {/* Snake head - positioned forward along Z, rotated to align with Z-axis */}
            <mesh
              position={[0, 0, 0.7]}
              rotation={[Math.PI / 2, 0, 0]}
              castShadow
            >
              <cylinderGeometry args={[0.15, 0.35, 0.5, 6]} />
              <meshLambertMaterial color={primaryColor} flatShading />
            </mesh>

            {/* Tail section - positioned backward along Z, rotated to align with Z-axis */}
            <mesh
              position={[0, 0, -0.7]}
              rotation={[Math.PI / 2, 0, 0]}
              castShadow
            >
              <cylinderGeometry args={[0.1, 0.25, 0.4, 6]} />
              <meshLambertMaterial color={primaryColor} flatShading />
            </mesh>

            {/* Red markings on back */}
            <mesh
              position={[0, 0.25, 0]}
              rotation={[Math.PI / 2, 0, 0]}
              castShadow
            >
              <cylinderGeometry args={[0.15, 0.15, 0.15, 5]} />
              <meshLambertMaterial color={secondaryColor} flatShading />
            </mesh>
            <mesh
              position={[0, 0.25, 0.3]}
              rotation={[Math.PI / 2, 0, 0]}
              castShadow
            >
              <cylinderGeometry args={[0.15, 0.15, 0.15, 5]} />
              <meshLambertMaterial color={secondaryColor} flatShading />
            </mesh>

            {/* Eyes - positioned on sides of the head */}
            <mesh position={[0.12, 0.12, 0.8]} castShadow>
              <sphereGeometry args={[0.06, 6, 6]} />
              <meshBasicMaterial color="black" />
            </mesh>
            <mesh position={[-0.12, 0.12, 0.8]} castShadow>
              <sphereGeometry args={[0.06, 6, 6]} />
              <meshBasicMaterial color="black" />
            </mesh>

            {/* Mouth line - at the front */}
            <mesh
              position={[0, 0, 0.95]}
              rotation={[Math.PI / 2, 0, 0]}
              castShadow
            >
              <boxGeometry args={[0.2, 0.01, 0.05]} />
              <meshBasicMaterial color="#1B1B1B" />
            </mesh>

            {/* Subtle underbelly - under the body */}
            <mesh position={[0, -0.15, 0]} rotation={[0, 0, 0]} castShadow>
              <boxGeometry args={[0.3, 0.1, 0.9]} />
              <meshLambertMaterial color="#66BB6A" flatShading />
            </mesh>
          </group>
        );
      })}
      {/* Boss - Orochimaru styled like SimpleNaruto with low-poly design */}
      <group
        position={boss.position}
        rotation={[0, Math.sin(Date.now() / 2000) * 0.3, bossSwayAngle]}
        scale={[1.5, 2.8, 1.5]}
      >
        {/* Main body - pale cream colored robe */}
        <mesh position={[0, 0.45, 0]} castShadow>
          <boxGeometry args={[0.9, 1.2, 0.45]} />
          <meshLambertMaterial color="#E1E1D0" flatShading />{' '}
          {/* Light cream colored robe */}
        </mesh>

        {/* Head */}
        <mesh position={[0, 1.2, 0]} castShadow>
          <boxGeometry args={[0.8, 0.8, 0.8]} />
          <meshLambertMaterial color="#F8F8FF" flatShading /> {/* Pale face */}
        </mesh>

        {/* Hair */}
        <mesh position={[0, 1.5, 0]} castShadow>
          <boxGeometry args={[0.9, 0.4, 0.9]} />
          <meshLambertMaterial color="#191919" flatShading /> {/* Black hair */}
        </mesh>

        {/* Long hair back */}
        <mesh position={[0, 1.0, -0.25]} castShadow>
          <boxGeometry args={[0.7, 1.0, 0.2]} />
          <meshLambertMaterial color="#191919" flatShading /> {/* Black hair */}
        </mesh>

        {/* Hair side strands */}
        <mesh position={[-0.45, 1.0, 0.1]} castShadow rotation={[0, 0, 0.1]}>
          <boxGeometry args={[0.15, 0.7, 0.25]} />
          <meshLambertMaterial color="#191919" flatShading /> {/* Black hair */}
        </mesh>
        <mesh position={[0.45, 1.0, 0.1]} castShadow rotation={[0, 0, -0.1]}>
          <boxGeometry args={[0.15, 0.7, 0.25]} />
          <meshLambertMaterial color="#191919" flatShading /> {/* Black hair */}
        </mesh>

        {/* Face markings */}
        <mesh position={[0, 1.3, 0.4]} castShadow>
          <boxGeometry args={[0.65, 0.15, 0.05]} />
          <meshLambertMaterial color="#8E44AD" flatShading />{' '}
          {/* Purple markings */}
        </mesh>

        {/* Eyes */}
        <mesh position={[-0.2, 1.35, 0.4]} castShadow>
          <boxGeometry args={[0.15, 0.1, 0.05]} />
          <meshLambertMaterial color="#FFCC00" flatShading />{' '}
          {/* Yellow snake eyes */}
        </mesh>
        <mesh position={[0.2, 1.35, 0.4]} castShadow>
          <boxGeometry args={[0.15, 0.1, 0.05]} />
          <meshLambertMaterial color="#FFCC00" flatShading />{' '}
          {/* Yellow snake eyes */}
        </mesh>

        {/* Snake wrapped around body */}
        <group rotation={[0, Math.sin(Date.now() / 1500) * 0.2, 0]}>
          {/* Main snake body - wrapped around torso */}
          <mesh position={[0, 0.3, 0.3]} castShadow>
            <boxGeometry args={[1.0, 0.25, 0.25]} />
            <meshLambertMaterial color="#5D4037" flatShading />{' '}
            {/* Brown snake body */}
          </mesh>
          <mesh position={[0.5, 0.3, 0]} castShadow>
            <boxGeometry args={[0.25, 0.25, 0.65]} />
            <meshLambertMaterial color="#5D4037" flatShading />
          </mesh>
          <mesh position={[0.5, 0, 0]} castShadow>
            <boxGeometry args={[0.25, 0.6, 0.25]} />
            <meshLambertMaterial color="#5D4037" flatShading />
          </mesh>
          <mesh position={[0, -0.3, 0]} castShadow>
            <boxGeometry args={[1.0, 0.25, 0.25]} />
            <meshLambertMaterial color="#5D4037" flatShading />
          </mesh>
          <mesh position={[-0.5, 0, 0]} castShadow>
            <boxGeometry args={[0.25, 0.6, 0.25]} />
            <meshLambertMaterial color="#5D4037" flatShading />
          </mesh>

          {/* Purple markings on snake */}
          <mesh position={[0.25, 0.3, 0.3]} castShadow>
            <boxGeometry args={[0.25, 0.26, 0.26]} />
            <meshLambertMaterial color="#9575CD" flatShading />{' '}
            {/* Purple snake sections */}
          </mesh>
          <mesh position={[0.5, 0.15, 0]} castShadow>
            <boxGeometry args={[0.26, 0.26, 0.25]} />
            <meshLambertMaterial color="#9575CD" flatShading />
          </mesh>
          <mesh position={[-0.25, -0.3, 0]} castShadow>
            <boxGeometry args={[0.25, 0.26, 0.26]} />
            <meshLambertMaterial color="#9575CD" flatShading />
          </mesh>

          {/* Snake head */}
          <group
            position={[-0.5, -0.3, 0.2]}
            rotation={[0, Math.PI / 2 + Math.sin(Date.now() / 1000) * 0.3, 0]}
          >
            <mesh position={[0, 0, 0.2]} castShadow>
              <boxGeometry args={[0.25, 0.2, 0.4]} />
              <meshLambertMaterial color="#5D4037" flatShading />
            </mesh>
            {/* Snake eyes */}
            <mesh position={[0.08, 0.05, 0.35]} castShadow>
              <boxGeometry args={[0.08, 0.07, 0.07]} />
              <meshLambertMaterial color="#000000" flatShading />
            </mesh>
            <mesh position={[-0.08, 0.05, 0.35]} castShadow>
              <boxGeometry args={[0.08, 0.07, 0.07]} />
              <meshLambertMaterial color="#000000" flatShading />
            </mesh>
          </group>
        </group>

        {/* Arms */}
        <group position={[0.55, 0.8, 0]} rotation={[bossArmRotation, 0, 0.2]}>
          <mesh position={[0, -0.35, 0]} castShadow>
            <boxGeometry args={[0.2, 0.7, 0.2]} />
            <meshLambertMaterial color="#E1E1D0" flatShading />
          </mesh>
        </group>
        <group
          position={[-0.55, 0.8, 0]}
          rotation={[-bossArmRotation, 0, -0.2]}
        >
          <mesh position={[0, -0.35, 0]} castShadow>
            <boxGeometry args={[0.2, 0.7, 0.2]} />
            <meshLambertMaterial color="#E1E1D0" flatShading />
          </mesh>
        </group>

        {/* Purple rope belt - signature Orochimaru element */}
        <mesh position={[0, -0.2, 0]} castShadow>
          <boxGeometry args={[1.0, 0.2, 0.55]} />
          <meshLambertMaterial color="#8E44AD" flatShading />{' '}
          {/* Purple belt */}
        </mesh>
      </group>
      {/* Boss health bar */}
      <group position={[0, 6.5, -30]}>
        <mesh scale={[(boss.health / 100) * 5, 0.3, 0.1]}>
          <boxGeometry />
          <meshBasicMaterial color="#cc2222" />
        </mesh>
        <Text
          position={[0, 0.5, 0]}
          color="white"
          fontSize={0.5}
          anchorX="center"
          anchorY="middle"
        >
          OROCHIMARU
        </Text>
      </group>
      {/* Title and instructions */}
      {!gameStarted ? (
        <>
          <Text
            position={[0, 4, 0]}
            color="white"
            fontSize={1.2}
            anchorX="center"
            anchorY="middle"
          >
            CONTRA: HOKAGE MISSION
          </Text>
          <Text
            position={[0, 3, 0]}
            color="#ffcc00"
            fontSize={0.5}
            anchorX="center"
            anchorY="middle"
          >
            Press ENTER to start
          </Text>
          <Text
            position={[0, 2.5, 0]}
            color="#ffffff"
            fontSize={0.3}
            anchorX="center"
            anchorY="middle"
          >
            WASD to move, F to throw shuriken, R for special attack
          </Text>
          <Text
            position={[0, 2, 0]}
            color="#ffcc00"
            fontSize={0.3}
            anchorX="center"
            anchorY="middle"
          >
            Defeat Oro and his snake minions!
          </Text>
          <Text
            position={[0, 2, 10]}
            color="white"
            fontSize={0.4}
            anchorX="center"
            anchorY="middle"
          >
            ESC to exit
          </Text>
        </>
      ) : null}
      {/* Hit Effects */}
      {hitEffects.map((effect, index) => {
        // Fade out based on time
        const age = Date.now() - effect.createdAt;
        const opacity = 1 - age / 500; // Fade out over 500ms

        if (opacity <= 0) return null; // Don't render if completely faded

        return (
          <group key={index} position={effect.position}>
            {/* Central flash */}
            <mesh>
              <sphereGeometry args={[0.3, 8, 8]} />
              <meshBasicMaterial
                color="#FFFFFF"
                transparent
                opacity={opacity}
              />
            </mesh>

            {/* Particles */}
            {[...Array(5)].map((_, i) => {
              const angle = (i / 5) * Math.PI * 2;
              const distance = 0.2 + (age / 500) * 0.5; // Expand outward over time

              return (
                <mesh
                  key={i}
                  position={[
                    Math.cos(angle) * distance,
                    Math.sin(angle) * distance,
                    0,
                  ]}
                >
                  <sphereGeometry args={[0.1, 6, 6]} />
                  <meshBasicMaterial
                    color="#FF4444"
                    transparent
                    opacity={opacity}
                  />
                </mesh>
              );
            })}
          </group>
        );
      })}
    </>
  );
};
